import React, { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "../styles/support-tickets.css";

export type SupportTicketSelectOption = {
    value: string;
    label: string;
};

type Props = {
    id?: string;
    value: string;
    options: SupportTicketSelectOption[];
    disabled?: boolean;
    placeholder?: string;
    /** Compact trigger for table filter rows */
    size?: "default" | "compact";
    "data-testid"?: string;
    onChange: (value: string) => void;
    onBlur?: () => void;
};

export default function SupportTicketSelect({
    id,
    value,
    options,
    disabled = false,
    placeholder = "Select…",
    size = "default",
    "data-testid": dataTestId,
    onChange,
    onBlur,
}: Props) {
    const generatedId = useId();
    const selectId = id ?? generatedId;
    const listboxId = `${selectId}-listbox`;
    const rootRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLUListElement>(null);
    const [open, setOpen] = useState(false);
    const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
    const [dropUp, setDropUp] = useState(false);

    const selected = options.find((opt) => opt.value === value);
    const displayLabel = selected?.label ?? placeholder;

    const updateMenuPosition = () => {
        const trigger = triggerRef.current;
        if (!trigger) return;
        const rect = trigger.getBoundingClientRect();
        const estimatedHeight = options.length * 40 + 16;
        const spaceBelow = window.innerHeight - rect.bottom - 8;
        const spaceAbove = rect.top - 8;
        const openUp = spaceBelow < estimatedHeight && spaceAbove > spaceBelow;
        setDropUp(openUp);
        setMenuStyle({
            position: "fixed",
            left: rect.left,
            width: Math.max(rect.width, 140),
            zIndex: 4000,
            ...(openUp
                ? { bottom: window.innerHeight - rect.top + 6, top: "auto" }
                : { top: rect.bottom + 6, bottom: "auto" }),
        });
    };

    useLayoutEffect(() => {
        if (!open) return;
        updateMenuPosition();
        const onReposition = () => updateMenuPosition();
        window.addEventListener("resize", onReposition);
        window.addEventListener("scroll", onReposition, true);
        return () => {
            window.removeEventListener("resize", onReposition);
            window.removeEventListener("scroll", onReposition, true);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- reposition when open/options change
    }, [open, options.length]);

    useEffect(() => {
        if (!open) return;
        const onPointerDown = (e: MouseEvent) => {
            const target = e.target as Node;
            if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) {
                return;
            }
            setOpen(false);
            onBlur?.();
        };
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                e.preventDefault();
                setOpen(false);
                onBlur?.();
            }
        };
        document.addEventListener("mousedown", onPointerDown);
        window.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("mousedown", onPointerDown);
            window.removeEventListener("keydown", onKeyDown);
        };
    }, [open, onBlur]);

    const handleSelect = (next: string) => {
        onChange(next);
        setOpen(false);
        // Do not call onBlur after select — parents may validate with stale value.
    };

    return (
        <div
            className={`support-ticket-select support-ticket-select--${size}${open ? " is-open" : ""}${
                disabled ? " is-disabled" : ""
            }${dropUp ? " is-drop-up" : ""}`}
            ref={rootRef}
            data-testid={dataTestId}
        >
            <button
                type="button"
                id={selectId}
                ref={triggerRef}
                className="support-ticket-select-trigger"
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-controls={listboxId}
                onClick={() => {
                    if (disabled) return;
                    setOpen((prev) => !prev);
                }}
            >
                <span className={`support-ticket-select-value${!selected ? " is-placeholder" : ""}`}>
                    {displayLabel}
                </span>
                <span className="support-ticket-select-chevron" aria-hidden="true" />
            </button>

            {open &&
                !disabled &&
                createPortal(
                    <ul
                        id={listboxId}
                        ref={menuRef}
                        className="support-ticket-select-menu"
                        role="listbox"
                        aria-labelledby={selectId}
                        style={menuStyle}
                    >
                        {options.map((opt) => {
                            const isSelected = opt.value === value;
                            return (
                                <li key={opt.value} role="presentation">
                                    <button
                                        type="button"
                                        role="option"
                                        aria-selected={isSelected}
                                        className={`support-ticket-select-option${
                                            isSelected ? " is-selected" : ""
                                        }`}
                                        onClick={() => handleSelect(opt.value)}
                                    >
                                        <span className="support-ticket-select-option-label">
                                            {opt.label}
                                        </span>
                                        {isSelected && (
                                            <span
                                                className="support-ticket-select-check"
                                                aria-hidden="true"
                                            >
                                                ✓
                                            </span>
                                        )}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>,
                    document.body,
                )}
        </div>
    );
}
